import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { cartService } from '../../services/cartService';
import { blogService } from '../../services/blogService';
import { cartEvents } from '../../utils/cartEventEmitter';
import Icon from '../AppIcon';
import Button from './Button';
import PageHelpModal from './PageHelpModal';
import SystemMessageBanner from './SystemMessageBanner';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useAuth();
  
  const user = authContext?.user || null;
  const userProfile = authContext?.userProfile || null;
  const loading = authContext?.loading ?? true;
  const signOut = authContext?.signOut;
  const isSeller = authContext?.isSeller ?? false;
  const isManufacturer = authContext?.isManufacturer ?? false;
  const isRepairShop = authContext?.isRepairShop ?? false;
  const isWrapShop = authContext?.isWrapShop ?? false;
  const isShop = authContext?.isShop ?? false;
  const isAdmin = authContext?.isAdmin ?? false;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isPageHelpOpen, setIsPageHelpOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [siteTitle, setSiteTitle] = useState('3D Interactive Parts Replacements');
  const dropdownRef = useRef(null);

  const navigationItems = [
    { name: 'Home', path: '/', icon: 'Home' },
    { name: 'Models', path: '/model-catalog', icon: 'Box' },
    { name: '3D Viewer', path: '/3d-model-viewer', icon: 'Eye' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window?.scrollY > 20);
    };
    window?.addEventListener('scroll', handleScroll);
    return () => window?.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const settings = await blogService.getPageSettings('site_config');
        if (settings) {
          setSiteTitle(settings.siteTitle || '3D Interactive Parts Replacements');
        }
      } catch (err) {
        console.error('Header - Error fetching site config:', err);
      }
    };
    fetchSiteConfig();
  }, []);

  // Fetch cart count when user changes, location changes, or a cart event is emitted
  useEffect(() => {
    const fetchCartCount = async () => {
      if (user?.id) {
        try {
          const count = await cartService?.getCartCount(user.id);
          setCartCount(count);
        } catch (error) {
          setCartCount(0);
        }
      } else {
        setCartCount(0);
      }
    };
    
    fetchCartCount();

    // Subscribe to manual cart update events
    const unsubscribe = cartEvents.subscribe(fetchCartCount);
    
    return () => {
      unsubscribe();
    };
  }, [user?.id]);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    if (isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserDropdownOpen]);

  const handleForceReset = async () => {
    try {
      if (signOut) await signOut();
      else await supabase.auth.signOut();
      
      localStorage.clear();
      sessionStorage.clear();
      
      if (window.navigator && navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      }
      window.location.href = '/signin?reset=true';
    } catch (err) {
      window.location.href = '/signin';
    }
  };

  const handleSignOut = async () => {
    try {
      if (signOut) await signOut();
      else await supabase.auth.signOut();
      setIsUserDropdownOpen(false);
      navigate('/signin');
    } catch (error) {
      setIsUserDropdownOpen(false);
      navigate('/signin');
    }
  };

  const getUserInitials = () => {
    const firstName = userProfile?.firstName || userProfile?.first_name || '';
    const lastName = userProfile?.lastName || userProfile?.last_name || '';
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  const isActivePath = (path) => location?.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-[10001] bg-card pointer-events-auto shadow-sm flex flex-col">
      <div className="flex items-center h-16 px-6 max-w-[1920px] mx-auto relative w-full border-b border-border">
        {/* Left: Logo */}
        <div className="flex-1 flex justify-start">
          <Link to="/" className="flex items-center space-x-3 group">
            <img 
              src="/assets/images/OpenGadget-SMALL-GREY-TRANS-1763756659192.png" 
              alt={`OpenGadget - ${siteTitle}`}
              className="h-10 w-auto group-hover:scale-105 transition-transform"
            />
          </Link>
        </div>

        {/* Center: Navigation (PERFECTLY CENTERED) */}
        <nav className="hidden lg:flex items-center justify-center space-x-1 flex-1">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm transition-all duration-200 ${
                isActivePath(item.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <Icon name={item.icon} size={16} />
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>

        {/* Right: Actions */}
        <div className="flex-1 flex justify-end items-center space-x-4">
          <Link to="/shopping-cart" className="relative p-2 text-muted-foreground hover:text-blue-600 transition-colors">
            <Icon name="ShoppingCart" size={24} />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Link>

          {loading ? (
            <div className="w-10 h-10 flex items-center justify-center">
              <Icon name="Loader2" size={20} className="animate-spin text-gray-400" />
            </div>
          ) : user ? (
            <div className="relative group/user h-full flex items-center" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted transition-colors focus:outline-none"
              >
                <div className="relative flex-shrink-0">
                  {(userProfile?.avatarUrl || userProfile?.avatar_url) ? (
                    <img 
                      src={userProfile?.avatarUrl || userProfile?.avatar_url} 
                      alt="User profile" 
                      className="h-9 w-9 rounded-full object-cover border-2 border-primary/20"
                    />
                  ) : (
                    <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shadow-sm">
                      {getUserInitials()}
                    </div>
                  )}
                </div>
                
                <div className="hidden md:block text-left max-w-[150px]">
                  <p className="text-sm font-semibold text-foreground leading-none truncate">
                    {userProfile?.firstName || userProfile?.first_name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate mt-1">
                    {user?.email}
                  </p>
                </div>

                <Icon name="ChevronDown" size={16} className="text-muted-foreground hidden lg:block group-hover/user:text-foreground transition-colors" />
              </button>

              <div className={`absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl transition-all duration-200 z-[10005] pointer-events-auto ${
                isUserDropdownOpen 
                  ? 'opacity-100 visible translate-y-0' 
                  : 'opacity-0 invisible -translate-y-2 lg:group-hover/user:opacity-100 lg:group-hover/user:visible lg:group-hover/user:translate-y-0'
              }`}>
                <div className="p-4 border-b border-gray-100 bg-gray-50/50 rounded-t-xl text-left">
                    <div className="flex items-center space-x-3 mb-1">
                        <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                            {getUserInitials()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">
                              {userProfile?.firstName || userProfile?.first_name} {userProfile?.lastName || userProfile?.last_name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                    </div>
                </div>
                <div className="py-2 text-left">
                  <Link to="/user-account-dashboard" onClick={() => setIsUserDropdownOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-muted transition-colors">
                    <Icon name="User" size={18} className={`mr-3 text-muted-foreground ${isActivePath('/user-account-dashboard') ? 'text-primary' : ''}`} /> Account Profile
                  </Link>
                  
                  {isSeller && (
                    <Link to="/seller-dashboard" onClick={() => setIsUserDropdownOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-muted transition-colors">
                        <Icon name="Briefcase" size={18} className="mr-3 text-muted-foreground" /> Seller Dashboard
                    </Link>
                  )}

                  {isShop && (
                    <Link to="/shop-dashboard" onClick={() => setIsUserDropdownOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-muted transition-colors">
                        <Icon name="Wrench" size={18} className="mr-3 text-muted-foreground" /> Services Garage
                    </Link>
                  )}

                  {isManufacturer && (
                    <Link to="/manufacturing-dashboard" onClick={() => setIsUserDropdownOpen(false)} className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-muted transition-colors">
                        <Icon name="Factory" size={18} className="mr-3 text-muted-foreground" /> Manufacturing Dashboard
                    </Link>
                  )}

                  <div className="border-t border-gray-100 my-1"></div>
                  <button onClick={handleSignOut} className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium text-left">
                    <Icon name="LogOut" size={18} className="mr-3 text-red-600" /> Sign Out
                  </button>
                  <button onClick={handleForceReset} className="w-full flex items-center px-4 py-2.5 text-[10px] text-gray-400 hover:text-red-500 transition-colors text-left italic">
                    Trouble signing in? Clear Cache & Reset
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Link to="/signin" className="p-2 text-muted-foreground hover:text-blue-600">
              <Icon name="User" size={24} />
            </Link>
          )}
        </div>
      </div>
      <SystemMessageBanner />
    </header>
  );
}
